/**
 * Analytics / audience / audit / reports endpoints (analytics agent). Shapes come from @windrop/contracts; the
 * thin console client (`./client`) carries cookies, problem+json and Idempotency-Key. The shell's local
 * `DashboardDto` in `admin.ts` is superseded by the contracts one used here.
 */
import type {
  AdminWithdrawConsentRequest,
  AudienceExportRequest,
  AuditPageDto,
  AuditQuery,
  CampaignReportDto,
  CreateExperimentRequest,
  DashboardDto,
  EngagementResultsDto,
  EraseParticipantRequest,
  EraseParticipantResponse,
  ExperimentDto,
  ExportAccessDto,
  ExportJobDto,
  ExportPageDto,
  HypercareReportDto,
  IncidentListDto,
  LotteryExportType,
  MetricArea,
  MetricsAreaDto,
  NextBusinessDayReportDto,
  ParticipantListQuery,
  ParticipantPageDto,
  ParticipantProfileDto,
  SegmentDto,
  SegmentPreviewDto,
  UpsertSegmentRequest,
} from '@windrop/contracts';
import { api } from './client';

export interface ExportDownloadDto {
  downloadUrl: string;
  expiresAt: string;
  watermark: string;
}

export const analyticsApi = {
  dashboard: (lotteryId?: string) => api.get<DashboardDto>('/v1/admin/dashboard', { lotteryId }),
  area: (area: MetricArea, query: { lotteryId?: string; from?: string; to?: string } = {}) =>
    api.get<MetricsAreaDto>(`/v1/admin/metrics/${area}`, query),
  campaignReport: (lotteryId: string) =>
    api.get<CampaignReportDto>(`/v1/admin/lotteries/${lotteryId}/report`),
  nextBusinessDay: (query: { date?: string; lotteryId?: string } = {}) =>
    api.get<NextBusinessDayReportDto>('/v1/admin/reports/next-business-day', query),
  hypercare: (lotteryId?: string) =>
    api.get<HypercareReportDto>('/v1/admin/reports/hypercare', { lotteryId }),
  experiments: () => api.get<ExperimentDto[]>('/v1/admin/experiments'),
  createExperiment: (body: CreateExperimentRequest) => api.post<ExperimentDto>('/v1/admin/experiments', body),
};

export const audienceApi = {
  segments: () => api.get<SegmentDto[]>('/v1/admin/audience/segments'),
  upsertSegment: (body: UpsertSegmentRequest) => api.post<SegmentDto>('/v1/admin/audience/segments', body),
  preview: (segmentId: string) =>
    api.get<SegmentPreviewDto>(`/v1/admin/audience/segments/${segmentId}/preview`),
  export: (body: AudienceExportRequest) => api.post<ExportJobDto>('/v1/admin/audience/export', body),
  participants: (query: Partial<ParticipantListQuery> = {}) =>
    api.get<ParticipantPageDto>('/v1/admin/audience/participants', query),
  profile: (participantId: string) =>
    api.get<ParticipantProfileDto>(`/v1/admin/participants/audience-profile/${participantId}`),
  withdraw: (participantId: string, body: AdminWithdrawConsentRequest) =>
    api.post<ParticipantProfileDto>(`/v1/admin/participants/${participantId}/consent/withdraw`, body),
  erase: (participantId: string, body: EraseParticipantRequest) =>
    api.post<EraseParticipantResponse>(`/v1/admin/participants/${participantId}/erase`, body),
  engagement: (lotteryId?: string) =>
    api.get<EngagementResultsDto>('/v1/admin/audience/engagement', { lotteryId }),
};

export const exportsApi = {
  list: (query: { kind?: string; lotteryId?: string } = {}) =>
    api.get<ExportPageDto>('/v1/admin/exports', query),
  get: (exportId: string) => api.get<ExportJobDto>(`/v1/admin/exports/${exportId}`),
  download: (exportId: string) => api.get<ExportDownloadDto>(`/v1/admin/exports/${exportId}/download`),
  access: (exportId: string) => api.get<ExportAccessDto[]>(`/v1/admin/exports/${exportId}/access`),
  lottery: (lotteryId: string, type: LotteryExportType) =>
    api.get<ExportJobDto>(`/v1/admin/lotteries/${lotteryId}/exports/${type}`),
};

export const auditConsoleApi = {
  list: (query: Partial<AuditQuery> & { cursor?: string; limit?: number }) =>
    api.get<AuditPageDto>('/v1/admin/audit', query),
  export: (query: Partial<AuditQuery>) => api.get<ExportJobDto>('/v1/admin/audit/export', query),
  incidents: () => api.get<IncidentListDto>('/v1/admin/audit/incidents'),
  accessGrants: () => api.get<AccessGrantRow[]>('/v1/admin/access-grants'),
};

/** Access grant row as served by identity (`AccessGrantDto`); only the fields this console renders. */
export interface AccessGrantRow {
  grantId?: string;
  id?: string;
  tenantId?: string;
  tenantSlug?: string;
  reason?: string;
  ticketRef?: string | null;
  grantedAt?: string;
  createdAt?: string;
  expiresAt?: string;
  revokedAt?: string | null;
}
